import { useMemo } from 'react';
import { DateField, FilterableField } from 'components/organisms/tasks/fields';
import {
  DateFieldFilter,
  DateFieldFilterOperationType,
  isOptional,
} from 'components/organisms/tasks/filters/date';
import { useProjectFieldsAsFilterableFields } from 'utils/project-fields/use-project-fields-as-filterable-fields';
import { useProjectsReportFields } from './use-projects-report-fields';

export const FIELD_ID_TEMPLATE = -1;
export const FIELD_ID_ARR = FIELD_ID_TEMPLATE - 1;
export const FIELD_ID_PROJECT_NAME = FIELD_ID_ARR - 1;
export const FIELD_ID_PROJECT_FEE = FIELD_ID_PROJECT_NAME - 1;
export const FIELD_ID_PROJECT_OWNER = FIELD_ID_PROJECT_FEE - 1;
export const FIELD_ID_CUSTOMER = FIELD_ID_PROJECT_OWNER - 1;
export const REPORT_FIELD_START = FIELD_ID_CUSTOMER - 1;

export const useFilterFields = (
  includeReportSpecificFields: boolean,
): { fields: FilterableField[]; isFieldsLoading: boolean } => {
  const { fields: projectFields, loading: isFieldsLoading } =
    useProjectFieldsAsFilterableFields();
  const projectsReportFields = useProjectsReportFields(REPORT_FIELD_START);
  const projectFilterableFields: FilterableField[] = useMemo(
    () =>
      projectFields
        .map((x) => ({
          ...x,
          fieldName: x.fieldName === 'Status' ? 'status' : x.fieldName,
        }))
        .filter(({ fieldName }) => fieldName !== 'partnerCompany'),
    [projectFields],
  );
  const fields = useMemo(
    () =>
      [
        ...projectFilterableFields,
        ...(includeReportSpecificFields ? projectsReportFields : []),
      ].filter((x) => Boolean(x)) as FilterableField[],
    [
      projectFilterableFields,
      projectsReportFields,
      includeReportSpecificFields,
    ],
  );

  return useMemo(
    () => ({
      fields,
      isFieldsLoading,
    }),
    [isFieldsLoading, fields],
  );
};

export const ReportsDateFieldFilterOperationTypes = [
  DateFieldFilterOperationType.IsThisMonth,
  DateFieldFilterOperationType.IsLastMonth,
  DateFieldFilterOperationType.IsThisQuarter,
  DateFieldFilterOperationType.IsLastQuarter,
  DateFieldFilterOperationType.IsAny,
  DateFieldFilterOperationType.IsMoreThan,
  DateFieldFilterOperationType.IsExactly,
  DateFieldFilterOperationType.IsLessThan,
  DateFieldFilterOperationType.IsAfter,
  DateFieldFilterOperationType.IsOn,
  DateFieldFilterOperationType.IsBefore,
  DateFieldFilterOperationType.IsEmpty,
  DateFieldFilterOperationType.IsNotEmpty,
];

export const dateFilterOperationOptions = (
  field: DateField,
): DateFieldFilter[] =>
  ReportsDateFieldFilterOperationTypes.filter(
    (x) => !field.metaData?.required || !isOptional(x),
  ).map(
    (type) =>
      ({
        field,
        type,
      } as any as DateFieldFilter),
  );
